// Place this at the top-level scope, outside the class (so it's only created once)
const htmlDecodeDiv = document.createElement('div');

window.formatMoney = function (cents, format) {
  if (typeof cents == 'string') { cents = cents.replace('.', ''); }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = (format || this.money_format);

  function defaultOption(opt, def) {
    return (typeof opt == 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) { return 0; }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? (decimal + parts[1]) : '';

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

// Base component class that all Mix&Match components will extend
class MixMatchComponent extends HTMLElement {
  constructor() {
    super();
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.unsubscribe = window.mixMatchStore.subscribe((state) =>
      this.onStateChange(state)
    );
  }

  disconnectedCallback() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  // Each component implements this method
  onStateChange({ action, previousState, state }) {
    // To be implemented by child classes
  }

  // Static utility method for normalizing text
  static normalizeText(text) {
    if (!text) return '';
    htmlDecodeDiv.innerHTML = text;
    return htmlDecodeDiv.textContent.trim();
  }
}

class MixAndMatchStore {
  constructor() {
    this.state = {
      totalPrice: 0,
      totalPriceWithoutDiscount: 0,
      totalQuantity: 0,
      minimumQuantity: 0,
      gifts: {},
      currentDiscount: null,
      variants: {},
    };

    this.previousState = null;
    this.discountTiers = [];
    this.subscribers = new Set();

    this.init();
  }

  init() {
    this.notify("INIT_STARTED");
    // load configuration from foxsell
    try {
      const mmConfig = sessionStorage.getItem("mm-config");
      if (!mmConfig) {
        throw new Error("Missing mix&match configuration");
      }

      const rawConfig = JSON.parse(mmConfig).options;
      window.mmConfig = JSON.parse(mmConfig);
      if (!Array.isArray(rawConfig)) {
        throw new Error("Invalid mix&match configuration format");
      }

      // Transform into simplified structure
      this.state.discountTiers = this.parseDiscountConfig(rawConfig);
      if (this.state.discountTiers.length > 0) {
        this.state.minimumQuantity = this.state.discountTiers[0].quantity;
      }
    } catch (err) {
      console.error("Initialization failed:", err);
    }

    try {
      const stateRaw = sessionStorage.getItem("mm-state");
      const giftsRaw = sessionStorage.getItem("mm-addon-settings");

      const parsedState = stateRaw ? JSON.parse(stateRaw) : null;
      const parsedGifts = giftsRaw ? JSON.parse(giftsRaw) : null;

      if (parsedState) {
        this.state = parsedState;

        if (
          parsedGifts &&
          (!parsedState.gifts || parsedState.gifts.length === 0)
        ) {
          this.state.gifts = parsedGifts;
        }
      } else if (parsedGifts) {
        this.state.gifts = parsedGifts;
      }
    } catch (err) {
      console.error("Initialization failed:", err);
    }

    this.notify("INIT_DONE");
    this.editBundle();
  }

  getState() {
    return this.state;
  }

  clearState() {
    for (const productKey in this.state.variants) {
      const product = this.state.variants[productKey];
      product.quantity = 0;
    }
    this.state.totalPrice = 0;
    this.state.totalPriceWithoutDiscount = 0;
    this.state.totalQuantity = 0;
    this.state.currentDiscount = 0;

    for (const giftKey in this.state.gifts) {
      const gift = this.state.gifts[giftKey];
      gift.gift_quantity = 1;
    }
  }

  persist() {
    sessionStorage.setItem("mm-state", JSON.stringify(this.state));
  }

  updateGiftCheckedState(state) {
    const { gift_qnt, gift_handle } = state;

    const gift = this.state.gifts[gift_handle];

    if (!gift || isNaN(gift_qnt) || !gift_handle) {
      console.warn("Invalid gift data");
      return;
    }

    gift.gift_quantity = gift_qnt;

    this.persist();
    this.notify("GIFT_CHECKED_STATE_UPDATED");
  }

  parseDiscountConfig(rawConfig) {
    return rawConfig
      .map((tier) => {
        const [variantId, config] = Object.entries(tier)[0];
        return {
          variantId,
          quantity: config.quantity,
          discount: config.price.value,
        };
      })
      .sort((a, b) => a.quantity - b.quantity); // Sort by quantity ascending
  }

  updateVariantQuantity(variant) {
    const {
      id,
      category,
      price,
      title,
      image,
      quantity,
      type,
      handle,
      inventory_quantity
    } = variant;
    const variantInStore = this.state.variants[`${category}_${id}`];

    if (!variantInStore && quantity >= 0) {
      this.state.variants[`${category}_${id}`] = {
        id,
        category,
        quantity: quantity,
        title,
        image,
        price,
        type,
        handle,
        inventory_quantity
      };
    } else if (variantInStore) {
      this.state.variants[`${category}_${id}`].quantity += quantity;
    } else {
      console.warn("Quantity update failed");
    }
    this.updateTotals();
    this.persist();
    this.notify("UPDATE_VARIANT_QUANTITY");
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    // Return unsubscribe function
    return () => this.subscribers.delete(callback);
  }

  notify(actionName) {
    const state = this.getState();
    const stateUpdate = {
      action: actionName,
      // For the first action, use currentState as previousState
      previousState: this.previousState ?? state,
      state: state,
    };

    this.subscribers.forEach((onStateChange) => onStateChange(stateUpdate));

    // Store current state as previous for next update
    this.previousState = { ...state };
  }

  updateTotals() {
    let totalQuantity = 0;
    let totalPrice = 0;
    let totalPriceWithoutDiscount = 0;

    for (const productKey in this.state.variants) {
      const product = this.state.variants[productKey];
      totalQuantity += product.quantity;
      totalPriceWithoutDiscount += product.price * product.quantity;
    }

    this.state.totalQuantity = totalQuantity;
    this.state.totalPriceWithoutDiscount = totalPriceWithoutDiscount;
    this.state.currentDiscount = this.calculateDiscount(totalQuantity);

    for (const productKey in this.state.variants) {
      const product = this.state.variants[productKey];
      const price = product.price / 100;
      const discountFactor = (100 - this.state.currentDiscount) / 100;
      const discountedPrice = Math.round(price * discountFactor * 10000 / 100);
      const totalProductPrice = discountedPrice * product.quantity;

      totalPrice += totalProductPrice;
    }

    this.state.totalPrice = totalPrice;
  }

  calculateDiscount(totalQuantity) {
    const matchingTier = this.state.discountTiers
      .filter((tier) => totalQuantity >= tier.quantity)
      .pop(); // Get last (highest) matching tier

    return matchingTier ? matchingTier.discount : 0;
  }

  setMainCTAText(status) {
    let buttonStatus = "";
    const bundle_cancel = document.querySelectorAll("[data-bundle-cancel]");
    const bundle_any_item = document.querySelectorAll("[data-bundle-any_item]");
    let bundle_button_text = [bundle_cancel, bundle_any_item];
    if (buttonStatus != status) {
      bundle_button_text.map((allButtons) => {
        allButtons.forEach((el) => {
          if (el.hasAttribute("data-bundle-" + status)) {
            el.classList.remove("jc-hidden");
            el.parentElement.removeAttribute("style");
          } else {
            el.classList.add("jc-hidden");
          }
        });
      });
      buttonStatus = status;
    }
  }

  async editBundle() {
    if (window.location.search.includes("edit_bundle")) {
      const [baseUrl, bundleId] = window.location.search.split("edit_bundle");
      history.replaceState("", "", `${location.pathname}${baseUrl}`);

      let cartItem = window.cart.items.find((e) => e.key == bundleId);
      if (!cartItem) {
        console.warn("Cart item not found");
        return;
      }

      const addonsListString = cartItem.properties._bundle_addons;
      const bundleItemsString =
        cartItem.properties["__foxsell:dynamic_add_on_bundle_items"];

      this.clearState();

      const currentVariants = this.getState().variants;
      JSON.parse(bundleItemsString).forEach((bundleItem) => {
        const id = bundleItem.variantId;
        const key = Object.keys(currentVariants).find((k) => k.includes(id));
        if (key) {
          this.state.variants[key].quantity = bundleItem.quantity || 0;
          this.updateTotals();
          this.notify("UPDATE_VARIANT_QUANTITY");
        } else {
          console.warn("No key found in MixStorage for variantId:", id);
        }
      });

      if (addonsListString) {
        addonsListString.split(",").forEach((addon) => {
          const [addonHandle, addonQuantityStr] = addon.split(":");
          const addonQuantity = parseInt(addonQuantityStr, 10);
          if (addonHandle && !isNaN(addonQuantity)) {
            this.updateGiftCheckedState({
              gift_qnt: addonQuantity,
              gift_handle: addonHandle,
            });
            this.notify("GIFT_CHECKED_STATE_UPDATED");
          } else {
            console.warn("Invalid addon entry:", addon);
          }
        });
      }

      // this.setMainCTAText("cancel");
      document.querySelectorAll("[data-mm-add-to-cart]").forEach((el) => {
        el.setAttribute("data-mm-add-to-cart", bundleId);
      });

      window.addEventListener("beforeunload", () => {
        this.clearState();
        this.updateTotals();
        this.persist();
      });
    }
  }
}

class MMAccordionManager {
  constructor() {
    this.categories = document.querySelectorAll('mix-and-match [data-mm-category-title][data-nav-title]');
    this.init();
  }

  init() {
    this.categories.forEach((category, index) => {
      // Set initial state - first accordion open, others closed
      this.openAccordionElement(category);

      // Add event listeners
      this.addEventListeners(category);
    });
  }

  addEventListeners(category) {
    const title = category.querySelector('[data-accordion-title]');

    // Title click to toggle (no separate button anymore)
    if (title) {
      title.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleAccordion(category);
      });
    }
  }

  toggleAccordion(category) {
    const isOpen = category.classList.contains('mm-accordion-open');

    if (isOpen) {
      this.closeAccordionElement(category);
    } else {
      this.openAccordionElement(category);
    }
  }

  openAccordion(categoryId) {
    const category = document.querySelector(`#category-${categoryId}`);
    if (category) {
      this.openAccordionElement(category);
    }
  }

  openAccordionElement(category) {
    const content = category.querySelector('[data-accordion-content]');
    const icon = category.querySelector('[data-accordion-title] span:last-child');

    category.classList.add('mm-accordion-open');

    if (content) {
      // Use grid-rows for smooth animation
      content.classList.remove('jc-grid-rows-[0fr]');
      content.classList.add('jc-grid-rows-[1fr]');
    }

    if (icon) {
      // Rotate icon to point down
      icon.classList.remove('jc-rotate-0');
      icon.classList.add('jc-rotate-90');
    }
  }

  closeAccordionElement(category) {
    const content = category.querySelector('[data-accordion-content]');
    const icon = category.querySelector('[data-accordion-title] span:last-child');

    category.classList.remove('mm-accordion-open');

    if (content) {
      // Use grid-rows for smooth animation
      content.classList.remove('jc-grid-rows-[1fr]');
      content.classList.add('jc-grid-rows-[0fr]');
    }

    if (icon) {
      // Rotate icon to point right
      icon.classList.remove('jc-rotate-90');
      icon.classList.add('jc-rotate-0');
    }
  }
}
class MMProductCard extends MixMatchComponent {
  static observedAttributes = ["qnt"];

  constructor() {
    super();
    this.category = this.dataset.category;
    this.productId = this.dataset.productId;
    this.handle = this.dataset.handle;
    this.variantId = this.dataset.mixProduct;
    this.title = this.dataset.variantTitle || "";
    this.image = this.dataset.image;
    this.originalPrice = parseInt((this.getVariantPrice(this.variantId) || 0) * 100);
    this.quantityValue = 1;

    // Detect display mode based on variant data
    this.displayMode = this.detectDisplayMode();

    // Parse available variants
    try {
      this.availableVariants = JSON.parse(this.dataset.variants || "[]");
      this.currentVariant =
        this.availableVariants.find((v) => v.id == this.variantId) ||
        this.availableVariants[0] ||
        null;
    } catch (e) {
      console.error("Error parsing variants:", e);
      this.availableVariants = [];
      this.currentVariant = null;
    }

    // Visual elements
    this.price = this.querySelectorAll(".js-price");
    this.comparePrice = this.querySelectorAll(".js-compare-price");
    this.input = this.querySelector(".js-qnt-input");
    this.discount = this.querySelector(".js-bundle-discount-label");
    this.addButton = this.querySelector(".js-plus.jc-mm-add-btn");
    this.plusButton = this.querySelector(".js-main-buttons .js-plus");
    this.variantSelectors = this.querySelectorAll(".mix-and-match-variant-selector");
    this.init();
  }

  detectDisplayMode() {
    // In separated mode, there will only be one variant in the data
    // In grouped mode, there will be multiple variants
    try {
      const variants = JSON.parse(this.dataset.variants || "[]");
      return variants.length === 1 ? 'separated' : 'grouped';
    } catch (e) {
      console.error("Error detecting display mode:", e);
      return 'grouped'; // Default to grouped mode
    }
  }

  init() {
    this.updateQuantity(0);
    this.setPrices();
    this.setDiscount();
    this.updateAvailability();
    this.updateQuantityDisplay();
    this.initVariantSelectors();

    // PLUS ADD TO BUNDLE CART
    this.querySelectorAll(".js-plus").forEach((el) => {
      el.addEventListener("click", () => {
        if (this.currentVariant && this.currentVariant.available) {
          this.updateQuantity(this.quantityValue);
        }
      });
    });

    this.querySelectorAll(".js-minus").forEach((el) =>
      el.addEventListener("click", () => {
        this.updateQuantity(-this.quantityValue);
      })
    );

    this.querySelectorAll(".js-remove").forEach((el) =>
      el.addEventListener("click", () => {
        this.updateQuantity(-this.getAttribute('qnt'));
      })
    );
  }

  initVariantSelectors() {
    // Only initialize variant selectors in grouped mode
    if (this.displayMode !== 'grouped') {
      return;
    }

    // Initialize variant selectors
    this.variantSelectors = this.querySelectorAll(".mix-and-match-variant-selector");
    // Set up custom dropdown functionality
    this.initCustomDropdowns();
  }

  initCustomDropdowns() {
    this.variantSelectors.forEach((selector) => {
      const trigger = selector.querySelector('.variant-dropdown-trigger');
      const menu = selector.querySelector('.variant-dropdown-menu');
      const options = selector.querySelectorAll('.dropdown-option');

      if (!trigger || !menu) return;

      // Toggle dropdown on trigger click
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleCustomDropdown(trigger, menu);
      });

      // Handle option selection
      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Don't allow selection of unavailable options
          if (option.classList.contains('unavailable')) {
            return;
          }

          this.selectCustomOption(selector, option, trigger, menu);
        });
      });

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        this.handleCustomDropdownKeyboard(e, trigger, menu, options);
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (e.target && e.target.closest && !e.target.closest('.mix-and-match-variant-selector')) {
        this.closeAllCustomDropdowns();
      }
    });

    // Initial availability update
    this.updateVariantAvailability();
  }

  toggleCustomDropdown(trigger, menu) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    // Close all other dropdowns first
    this.closeAllCustomDropdowns();

    if (!isOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      menu.classList.remove('jc-hidden');
      trigger.focus();
    }
  }

  closeAllCustomDropdowns() {
    this.variantSelectors.forEach((selector) => {
      const trigger = selector.querySelector('.variant-dropdown-trigger');
      const menu = selector.querySelector('.variant-dropdown-menu');

      if (trigger && menu) {
        trigger.setAttribute('aria-expanded', 'false');
        menu.classList.add('jc-hidden');
      }
    });
  }

  selectCustomOption(selector, option, trigger, menu) {
    const value = option.dataset ? option.dataset.value : option.getAttribute('data-value');
    const selectedValueSpan = trigger.querySelector('.selected-value');
    const selectedValueIcon = trigger.querySelector('.js-icon');
    const optionValueIcon = option.querySelector('.js-icon');

    // Update UI
    selectedValueSpan.textContent = value;
    if (selectedValueSpan.dataset) {
      selectedValueSpan.dataset.value = value;
      selectedValueIcon.outerHTML = optionValueIcon.outerHTML;
    } else {
      selectedValueSpan.setAttribute('data-value', value);
    }

    // Update selected state - remove from all options first
    selector.querySelectorAll('.dropdown-option').forEach(opt => {
      opt.classList.remove('selected');
      opt.setAttribute('aria-selected', 'false');
    });

    // Add selected state to clicked option
    option.classList.add('selected');
    option.setAttribute('aria-selected', 'true');

    // Close dropdown
    trigger.setAttribute('aria-expanded', 'false');
    menu.classList.add('jc-hidden');

    // Trigger variant change
    this.handleVariantChange();
  }

  handleCustomDropdownKeyboard(e, trigger, menu, options) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.toggleCustomDropdown(trigger, menu);
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          this.closeAllCustomDropdowns();
          trigger.focus();
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (isOpen) {
          e.preventDefault();
          this.navigateDropdownOptions(options, e.key === 'ArrowDown' ? 1 : -1);
        }
        break;
    }
  }

  navigateDropdownOptions(options, direction) {
    const availableOptions = Array.from(options).filter(opt => !opt.classList.contains('unavailable'));
    const currentHighlighted = availableOptions.find(opt => opt.classList.contains('jc-bg-black/5'));

    // Remove current highlight
    availableOptions.forEach(opt => opt.classList.remove('jc-bg-black/5'));

    let nextIndex = 0;
    if (currentHighlighted) {
      const currentIndex = availableOptions.indexOf(currentHighlighted);
      nextIndex = (currentIndex + direction + availableOptions.length) % availableOptions.length;
    }

    const nextOption = availableOptions[nextIndex];
    if (nextOption) {
      nextOption.classList.add('jc-bg-black/5');
      nextOption.focus();
    }
  }

  handleVariantChange() {
    const selectedOptions = [];

    // Get selected values from custom dropdowns
    this.variantSelectors.forEach((selector) => {
      const selectedValue = selector.querySelector('.selected-value');
      if (selectedValue) {
        selectedOptions.push(this.normalizeText(selectedValue.dataset.value));
      }
    });

    // Find matching variant
    const variantsToSearch = this.getFoxsellVariants();

    let matchingVariant = variantsToSearch.find((variant) => {
      const isMatch = variant.options && variant.options.every(
        (option, index) => {
          const matches = option === selectedOptions[index];
          return matches;
        }
      );
      
      return isMatch;
    });

    if (matchingVariant) {
      this.switchToVariant(matchingVariant);
      this.updateVariantAvailability();
    } else {
      console.error("No variant could be matched for selected options:", {
        selectedOptions,
        availableVariants: variantsToSearch
      });
    }
  }

  updateVariantAvailability() {
    // Get current selections
    const currentSelections = this.getCurrentSelections();

    this.variantSelectors.forEach((selector, selectorIndex) => {
      const options = selector.querySelectorAll('.dropdown-option');
      const optionPosition = parseInt(selector.dataset.optionPosition) - 1;

      options.forEach((option) => {
        const value = option.dataset.value;

        // Create a test selection array with this option value
        const testSelections = [...currentSelections];
        testSelections[optionPosition] = value;

        const isIncluded = this.isOptionIncludedInbundle(testSelections);

        if (!isIncluded) {
          option.remove();
        }

        const isAvailable = this.isOptionAvaiable(testSelections);

        // Update option styling with Tailwind classes
        option.classList.remove('available', 'unavailable');
        option.classList.add(isAvailable ? 'available' : 'unavailable');

        if (!isAvailable) {
          option.setAttribute('disabled', 'true');

          // Override hover state for unavailable options
          option.classList.remove('hover:jc-bg-mistyRose');
        } else {
          // Remove unavailable classes and restore available state
          option.classList.add('hover:jc-bg-mistyRose');
          option.removeAttribute('disabled');
        }
      });
    });
  }

  getCurrentSelections() {
    const selections = [];

    this.variantSelectors.forEach((selector) => {
      const selectedValue = selector.querySelector('.selected-value');
      if (selectedValue) {
        selections.push(selectedValue.dataset.value);
      }
    });

    return selections;
  }

  isOptionAvaiable(value) {
    const variantsToSearch = this.getFoxsellVariants();
    const currentVariant = variantsToSearch.find((variant) => variant.title.replaceAll('&apos;', "'") === value[0]);

    if (!currentVariant) return false;

    return currentVariant.available || (!currentVariant.available && currentVariant.inventory_policy === 'continue') ? true : false;
  }

  isOptionIncludedInbundle(testSelections) {
    const variantsToSearch = this.getFoxsellVariants();
    return variantsToSearch.some((variant) => {
      // Check if this variant matches the test selections
      return variant.options.every((option, index) => {
        if (option.includes('&apos;')) {
          option = option.replaceAll('&apos;', "'");
        }
        // If we don't have a test selection for this position, it's a match
        if (testSelections[index] === undefined) return true;
        // Otherwise, check if the option matches
        return option === testSelections[index];
      });
    });
  }



  getFoxsellVariantIds() {
    try {
      // Get FoxSell variant data from the dataset
      const foxsellData = this.dataset.foxsellVariants;
      if (foxsellData) {
        const foxsellVariants = JSON.parse(foxsellData);
        // Extract variant IDs from the FoxSell variants object (keys are variant IDs)
        return Object.keys(foxsellVariants);
      }

      console.warn("No FoxSell variant data found");
    } catch (error) {
      console.warn("Error getting FoxSell variant IDs:", error);
      return [];
    }
  }

  filterSelectorOptions(selector, allowedValues) {
    const options = selector.querySelectorAll("option");

    options.forEach((option) => {
      const value = option.value;

      // Keep the option if it's in the allowed values
      if (allowedValues.has(value)) {
        option.style.display = "";
        option.disabled = false;
      } else {
        // Hide or disable options not in FoxSell variants
        option.style.display = "none";
        option.disabled = true;
      }
    });

    // Ensure a valid option is selected
    this.ensureValidSelection(selector, allowedValues);
  }

  ensureValidSelection(selector, allowedValues) {
    const currentValue = selector.value;

    // If current selection is not in allowed values, select the first allowed value
    if (!allowedValues.has(currentValue)) {
      const firstAllowed = Array.from(allowedValues)[0];
      if (firstAllowed) {
        selector.value = firstAllowed;
        // Trigger change to update other selectors and variant
        selector.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  getFoxsellVariants() {
    try {
      const foxsellVariantIds = this.getFoxsellVariantIds();
      const variants = this.availableVariants.filter((variant) =>
        foxsellVariantIds.includes(variant.id.toString())
      );
      
      // Normalize variant data
      return variants.map(variant => ({
        ...variant,
        title: this.normalizeText(variant.title),
        options: variant.options?.map(option => this.normalizeText(option))
      }));
    } catch (error) {
      console.warn("Error getting FoxSell variants:", error);
      return this.availableVariants;
    }
  }

  switchToVariant(newVariant) {
    // Update component properties to point to new variant
    this.variantId = newVariant.id;
    this.currentVariant = newVariant;
    this.originalPrice = (this.getVariantPrice(this.variantId) || 0) * 100;

    // Update data attributes
    this.dataset.mixProduct = newVariant.id;
    this.dataset.originalPrice = newVariant.price;

    // Update variant image
    this.updateVariantImage(newVariant);

    // Update UI elements for new variant
    this.setPrices();
    this.setDiscount();
    this.updateAvailability();
    this.updateQuantityDisplay();
  }

  updateVariantImage(variant) {
    let imageUrl = null;
    let imageAlt = variant.title || this.title;

    // Try to get variant image first
    if (variant.featured_image && variant.featured_image.src) {
      imageUrl = variant.featured_image.src;
    } else if (variant.image && variant.image.src) {
      imageUrl = variant.image.src;
    }

    // If no variant image, keep current image (don't change)
    if (!imageUrl) {
      return;
    }

    // Format image URL for Shopify (ensure proper size)
    const formattedImageUrl = this.formatShopifyImageUrl(imageUrl, "160x160");

    // Update data attribute
    this.dataset.image = formattedImageUrl;

    // Update all product images in the card
    const productImage = this.querySelector("img");
    if (!productImage) return;
    productImage.src = formattedImageUrl;
    productImage.alt = imageAlt;
    productImage.srcset = this.generateImageSrcset(imageUrl);
  }

  formatShopifyImageUrl(originalUrl, size = "160x160") {
    try {
      // Remove any existing size parameters
      const cleanUrl = originalUrl.split("?")[0];

      // Add new size parameter
      if (
        cleanUrl.includes("cdn.shopify.com") ||
        cleanUrl.includes("shopify.com")
      ) {
        // For Shopify images, use the image transform format
        const urlParts = cleanUrl.split(".");
        const extension = urlParts.pop();
        const baseUrl = urlParts.join(".");
        return `${baseUrl}_${size}.${extension}`;
      }

      return originalUrl; // Return original if not a Shopify image
    } catch (error) {
      console.warn("Error formatting image URL:", error);
      return originalUrl;
    }
  }

  generateImageSrcset(originalUrl) {
    const sizes = ["160x", "320x", "480x", "640x", "800x"];
    return sizes
      .map((size) => {
        const formattedUrl = this.formatShopifyImageUrl(originalUrl, size);
        const width = size.replace("x", "");
        return `${formattedUrl} ${width}w`;
      })
      .join(", ");
  }

  updateQuantityDisplay() {
    // Update quantity input to show quantity for currently selected variant
    const currentQuantity = this.getCurrentQuantity();
    if (this.input) {
      this.input.setAttribute("value", String(currentQuantity));
    }
    this.setAttribute("qnt", String(currentQuantity));
  }

  getCurrentQuantity() {
    if (!window.mixMatchStore) return 0;
    const state = window.mixMatchStore.getState();
    const variantKey = `${this.category}_${this.variantId}`;
    return state.variants[variantKey] ? state.variants[variantKey].quantity : 0;
  }

  updateAvailability() {
    // Update input max value
    if (
      this.input &&
      this.currentVariant
    ) {
      let inventory = this.currentVariant.inventory_quantity || 0;
      if (!this.currentVariant.inventory_management || this.currentVariant.inventory_policy === "continue") {
        inventory = 999;
      }
      this.input.setAttribute("max", String(inventory));
      this.input.setAttribute("data-in-stock", String(inventory));
    }
    const isAvailable = parseInt(this.input.getAttribute("value")) < parseInt(this.input.getAttribute("max"));
    // Update add button state
    if (this.addButton) {
      // check if selected quantity is less than inventory
      if (isAvailable) {
        this.addButton.classList.remove(
          "jc-opacity-50",
          "jc-pointer-events-none"
        );
        const buttonText = this.addButton.querySelector("p");
        if (buttonText) buttonText.textContent = "Add";
      } else {
        this.addButton.classList.add("jc-opacity-50", "jc-pointer-events-none");
        const buttonText = this.addButton.querySelector("p");
        if (buttonText) buttonText.textContent = "Sold out";
      }
    }
    if (this.plusButton) {
      if (isAvailable) {
        this.plusButton.classList.remove(
          "jc-opacity-50",
          "jc-pointer-events-none"
        );
      } else {
        this.plusButton.classList.add(
          "jc-opacity-50",
          "jc-pointer-events-none"
        );
      }
    }
  }

  updateQuantity(quantity) {
    if (
      !this.currentVariant ||
      !this.currentVariant.available ||
      !window.mixMatchStore
    ) {
      return;
    }

    window.mixMatchStore.updateVariantQuantity({
      id: this.variantId,
      category: this.category,
      price: this.originalPrice,
      title: this.title,
      image: this.dataset.image,
      quantity: quantity,
      type: "product",
      handle: this.handle,
      inventory_quantity: this.input.getAttribute("max"),
    });
  }

  getVariantPrice(variantId) {
    if (!window.mmConfig || !window.mmConfig.product_properties) return null;

    for (const productKey in window.mmConfig.product_properties) {
      const product = window.mmConfig.product_properties[productKey];
      if (
        product.variants &&
        product.variants[`gid://shopify/ProductVariant/${variantId}`]
      ) {
        return product.variants[`gid://shopify/ProductVariant/${variantId}`];
      }
    }
    console.error("Variant not found");
    return null;
  }

  // update percentage and price on product card
  setPrices() {
    if (!window.mixMatchStore) return;

    const state = window.mixMatchStore.getState();
    const variantKey = `${this.category}_${this.variantId}`;

    if (!state.variants[variantKey]) return;

    let priceFromStorage = Math.ceil(state.variants[variantKey].price);
    const discountFactor = (100 - state.currentDiscount) / 100;
    let price = priceFromStorage / 100;
    price = Math.round(price * discountFactor * 10000 / 100);
    // let price = Math.ceil(priceFromStorage * (1 - state.currentDiscount / 100));
    let comparePrice = priceFromStorage;

    this.price.forEach((el) => {
      el.setAttribute(
        "data-price",
        window.formatMoney(price, window.moneyFormat)
      );
      if (el.hasAttribute("data-price-q")) {
        el.setAttribute(
          "data-price-q",
          window.formatMoney(
            price * state.variants[variantKey].quantity,
            window.moneyFormat
          )
        );
      }
    });

    this.comparePrice.forEach((el) => {
      if (price == comparePrice) {
        el.setAttribute("data-compare-price", "0");
        if (el.hasAttribute("data-compare-price-q")) {
          el.setAttribute("data-compare-price-q", "0");
        }
      } else {
        el.setAttribute(
          "data-compare-price",
          window.formatMoney(comparePrice, window.moneyFormat)
        );
        if (el.hasAttribute("data-compare-price-q")) {
          el.setAttribute(
            "data-compare-price-q",
            window.formatMoney(
              comparePrice * state.variants[variantKey].quantity,
              window.moneyFormat
            )
          );
        }
      }
    });
  }

  setDiscount() {
    if (!window.mixMatchStore) return;

    const state = window.mixMatchStore.getState();
    if (this.discount) {
      if (state.currentDiscount == 0) {
        this.discount.innerText = "";
      } else {
        this.discount.innerText = `-${state.currentDiscount}%`;
      }
    }
  }

  removeFromDomMiniCard() {
    if (this.closest("product-mm-list")) {
      this.remove();
    }
  }

  onStateChange({ action, previousState, state }) {
    this.setPrices();
    this.setDiscount();

    if (action === "UPDATE_VARIANT_QUANTITY") {
      // Update quantity display for currently selected variant
      this.updateQuantityDisplay();

      this.updateAvailability();

      // Check if any variant of this product has quantity > 0 for mini-card removal
      const hasAnyQuantity = Object.keys(state.variants).some((key) => {
        const variant = state.variants[key];
        return (
          key.includes(`_${this.variantId}`) ||
          (variant.handle === this.handle && variant.quantity > 0)
        );
      });

      // Only remove from DOM if NO variants of this product have quantity
      if (!hasAnyQuantity) {
        this.removeFromDomMiniCard();
      }
    }
  }

  normalizeText(text) {
    if (!text) return '';
    htmlDecodeDiv.innerHTML = text;
    return htmlDecodeDiv.textContent.trim();
  }
}

class MMCartSummary extends MixMatchComponent {
  constructor() {
    super();

    // Visual elements
    this.totalQuantity = this.querySelectorAll(".js-total-qnt");
    this.totalPrice = this.querySelector(".js-total-price");
    this.totalPriceWithoutDiscount = this.querySelector(
      ".js-total-price-without-discount"
    );
    this.progress = this.querySelectorAll(".js-bundle-progress");
    this.mainAddBtns = this.querySelectorAll(".js-add-mix-and-match");
    this.giftCheckboxes = this.querySelectorAll("[name^='addon_gift']");

    // Add to cart button
    this.addToCartButton = () => this.querySelector(".js-add-mix-and-match");

    // Check if drawer mode is enabled
    const settings = this.dataset.settings ? JSON.parse(this.dataset.settings) : {};

    // Drawer elements - only try to find them if drawer mode is enabled
    if (settings.drawer_mode) {
      this.validateBundleBtn = this.querySelector(".js-validate-bundle");
      this.bundleDrawer = this.querySelector(".js-bundle-drawer");
      this.drawerOverlay = this.querySelector(".js-drawer-overlay");
      this.drawerCloseBtn = this.querySelector(".js-close-drawer");
      this.drawerContinueBtn = this.querySelector(".js-continue-drawer");
    } else {
      console.warn('Drawer mode disabled, skipping drawer element selection');
    }

    const state = window.mixMatchStore.getState();
    this.init();
    this.addProductMiniCards(state);
    this.updateGiftUI(state);
    this.updateTotalQuantity(state);
    this.updateTotalPrices(state);
    this.updateButtonsState(state);
    this.updateProgressBar(state);
    this.updateMessages(state);
    this.updateBottomCards();

    this.setGiftState(state);
    this.initGiftStateAndListener(state);
    this.showStickyOnIntersecting();
    this.initDrawer();
  }

  get contentWrapper() {
    if (!this._contentWrapper) {
      this._contentWrapper = this.querySelector(".js-content-wrapper");
    }
    return this._contentWrapper;
  }

  get content() {
    if (!this._content) {
      this._content = this.querySelector(".js-content");
    }
    return this._content;
  }

  get settings() {
    if (!this._settings) {
      this._settings = JSON.parse(this.dataset.settings);
    }
    return this._settings;
  }

  init() {
    if (this.addToCartButton()) {
      this.addToCartButton().addEventListener("click", () => {
        this.addToCart();
      });
    }
  }

  initDrawer() {
    // Initialize drawer functionality if validate bundle button exists
    if (this.validateBundleBtn) {
      this.validateBundleBtn.addEventListener("click", () => {
        this.openDrawer();
      });
    } else {
      console.warn('Validate bundle button not found');
    }

    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener("click", () => {
        this.closeDrawer();
      });
    }

    if (this.drawerCloseBtn) {
      this.drawerCloseBtn.addEventListener("click", () => {
        this.closeDrawer();
      });
    }

    if (this.drawerContinueBtn) {
      this.drawerContinueBtn.addEventListener("click", () => {
        this.closeDrawer();
      });
    }

    // Handle drawer add to cart button
    const drawerAddBtn = this.querySelector(".js-drawer-add-to-cart");
    if (drawerAddBtn) {
      drawerAddBtn.addEventListener("click", () => {
        this.addToCart();
      });
    }
  }

  openDrawer() {
    if (this.bundleDrawer) {
      document.body.style.overflow = 'hidden';

      // Show the drawer overlay
      this.bundleDrawer.classList.remove('jc-opacity-0', 'jc-pointer-events-none', 'jc-invisible');
      this.bundleDrawer.classList.add('jc-opacity-100', 'jc-pointer-events-auto', 'jc-visible');

      // Slide up the drawer content
      const drawerContent = this.bundleDrawer.querySelector('.js-drawer-content');
      if (drawerContent) {
        // Use setTimeout to ensure the transition is visible
        setTimeout(() => {
          drawerContent.classList.remove('jc-translate-y-full');
          drawerContent.classList.add('jc-translate-y-0');
        }, 10);
      }

      this.populateDrawerProducts();
    } else {
      console.warn('Bundle drawer element not found');
    }
  }

  closeDrawer() {
    if (this.bundleDrawer) {
      // Slide down the drawer content first
      const drawerContent = this.bundleDrawer.querySelector('.js-drawer-content');
      if (drawerContent) {
        drawerContent.classList.remove('jc-translate-y-0');
        drawerContent.classList.add('jc-translate-y-full');
      }

      // Hide the drawer overlay after animation
      setTimeout(() => {
        this.bundleDrawer.classList.remove('jc-opacity-100', 'jc-pointer-events-auto', 'jc-visible');
        this.bundleDrawer.classList.add('jc-opacity-0', 'jc-pointer-events-none', 'jc-invisible');
        document.body.style.overflow = '';
      }, 300); // Match the transition duration
    }
  }

  populateDrawerProducts() {
    const state = window.mixMatchStore.getState();
    const drawerProductList = this.querySelector(".js-drawer-product-list");
    const drawerProductTemplate = this.querySelector("#template-drawer-mm-card");

    if (!drawerProductList || !drawerProductTemplate) return;

    // Clear existing products
    drawerProductList.innerHTML = '';

    // Add products using desktop-style cards
    Array.from(Object.values(state.variants))
      .filter((variant) => variant.quantity != 0)
      .forEach((variant) => {
        let drawerProductCard = drawerProductTemplate.innerHTML;
        let variantTitle = this.getVariantDisplayTitle(variant);
        let variantOption = '';

        if (variantTitle.includes(' - ')) {
          const variantTitleParts = variantTitle.split('-');
          variantTitle = variantTitleParts[0];
          variantOption = variantTitleParts[1];
        }

        drawerProductCard = drawerProductCard.replaceAll("[variant_id]", variant.id);
        drawerProductCard = drawerProductCard.replaceAll("[variant_category]", variant.category);
        drawerProductCard = drawerProductCard.replaceAll("[variant_handle]", variant.handle || "");
        drawerProductCard = drawerProductCard.replaceAll("[variant_original_price]", window.formatMoney(variant.price, window.moneyFormat));
        drawerProductCard = drawerProductCard.replaceAll("[variant_title]", variantTitle);
        drawerProductCard = drawerProductCard.replaceAll("[variant_option]", variantOption);
        drawerProductCard = drawerProductCard.replaceAll("[variant_image]", variant.image);
        drawerProductCard = drawerProductCard.replaceAll("[variant_current_value]", variant.quantity);
        drawerProductCard = drawerProductCard.replaceAll("[variant_max_available]", variant.inventory_quantity);

        drawerProductList.insertAdjacentHTML("beforeend", drawerProductCard);
      });

    // Attach event listeners to drawer cards
    this.attachDrawerCardEventListeners(drawerProductList);

    // Update drawer totals
    this.updateDrawerTotals(state);
  }

  attachDrawerCardEventListeners(container) {
    const drawerCards = container.querySelectorAll("mm-product-card:not([data-drawer-listeners-attached])");

    drawerCards.forEach((card) => {
      const variantId = card.dataset.mixProduct;
      const category = card.dataset.category;
      const quantity = card.getAttribute('qnt');
      const handle = card.dataset.handle || this.getHandleFromVariant(variantId);

      const plusBtn = card.querySelector(".js-plus");
      const minusBtn = card.querySelector(".js-minus");
      const removeBtn = card.querySelector(".js-remove");

      if (plusBtn) {
        plusBtn.addEventListener("click", () => {
          this.updateMiniCardQuantity(variantId, category, handle, 1);
        });
      }

      if (minusBtn) {
        minusBtn.addEventListener("click", () => {
          this.updateMiniCardQuantity(variantId, category, handle, -1);
        });
      }

      if (removeBtn) {
        removeBtn.addEventListener("click", () => {
          this.updateMiniCardQuantity(variantId, category, handle, -quantity);
        });
      }

      card.setAttribute("data-drawer-listeners-attached", "true");
    });
  }

  updateDrawerTotals(state) {
    const drawerTotalPrice = this.querySelector(".js-drawer-total-price");
    const drawerTotalPriceWithoutDiscount = this.querySelector(".js-drawer-total-price-without-discount");
    const drawerTotalQuantity = this.querySelectorAll(".js-drawer-total-qnt");

    if (drawerTotalPrice) {
      drawerTotalPrice.textContent = state.totalPrice == 0 ? "" : window.formatMoney(state.totalPrice, window.moneyFormat);
    }

    if (drawerTotalPriceWithoutDiscount) {
      if (state.totalPriceWithoutDiscount == 0 || state.totalPriceWithoutDiscount == state.totalPrice) {
        drawerTotalPriceWithoutDiscount.textContent = "";
      } else {
        drawerTotalPriceWithoutDiscount.textContent = window.formatMoney(state.totalPriceWithoutDiscount, window.moneyFormat);
      }
    }

    if (drawerTotalQuantity.length) {
      drawerTotalQuantity.forEach(el => el.textContent = state.totalQuantity);
    }
  }

  setGiftState(state) {
    if (!this.giftCheckboxes.length) return;

    this.giftCheckboxes.forEach((checkbox) => {
      const giftHandle = checkbox.getAttribute("data-gift-handle");
      const giftData = state.gifts[giftHandle];

      if (giftData && typeof giftData.gift_quantity === "number") {
        checkbox.checked = giftData.gift_quantity > 0;
      }
    });
  }

  initGiftStateAndListener(state) {
    if (this.giftCheckboxes.length == 0) return;

    this.giftCheckboxes.forEach((el) => {
      const giftHandle = el.getAttribute("data-gift-handle");
      if (!state.gifts[giftHandle]) return;
      this.updateGiftCheckedState(
        state.gifts[giftHandle].gift_quantity,
        giftHandle
      );

      el.addEventListener("change", (event) => {
        const giftQnt = event.target.checked ? 1 : 0;
        const giftHandle = event.target.getAttribute("data-gift-handle");
        this.updateGiftCheckedState(giftQnt, giftHandle);
      });
    });
  }

  isBottomCart() {
    return this.classList.contains("js-bottom-bar");
  }

  showStickyOnIntersecting() {
    const mmSection = document.querySelector("mix-and-match");
    const stickyMMBar = this;
    if (!mmSection || !this.isBottomCart()) return;

    let options = {
      rootMargin: `0px 0px -200px 0px`,
    };

    const callbackAtcBtn = (entries, observer) => {
      entries.forEach((entrie) => {
        if (entrie.isIntersecting) {
          stickyMMBar.classList.add("js-active");
        } else {
          stickyMMBar.classList.remove("js-active");
        }
      });
    };

    const observerMMSection = new IntersectionObserver(callbackAtcBtn, options);

    mmSection ? observerMMSection.observe(mmSection) : null;
  }

  updateBottomCards() {
    if (!this.isBottomCart()) {
      return;
    }
    const bottom_product_cards = this.querySelectorAll("mm-product-card");

    this.querySelectorAll(".js-bottom-empty-product").forEach((el, i) => {
      if (i < bottom_product_cards.length) {
        el.classList.add("jc-hidden");
      } else {
        el.classList.remove("jc-hidden");
      }
    });
  }

  addProductMiniCards(state) {
    let productMiniCards = "";
    const asideProductMiniCardTmp = this.querySelector(
      "#template-aside-mm-card"
    )?.cloneNode(true);
    if (!asideProductMiniCardTmp) {
      console.warn("Aside card not found");
      return;
    }

    const target = this.isBottomCart()
      ? this.querySelector("product-mm-list .js-products-holder")
      : this.querySelector("product-mm-list");

    // Clear existing mini-cards to prevent duplicates
    if (target) {
      const existingCards = target.querySelectorAll("mm-product-card");
      existingCards.forEach((card) => card.remove());
    }

    Array.from(Object.values(state.variants))
      .filter((variant) => variant.quantity != 0)
      .forEach((variant) => {
        let asideProductMiniCard = asideProductMiniCardTmp.innerHTML;
        // Get variant options for display
        let variantTitle = this.getVariantDisplayTitle(variant);
        let variantOption = '';

        if (variantTitle.includes(' - ')) {
          const variantTitleParts = variantTitle.split('-');
          variantTitle = variantTitleParts[0];
          variantOption = variantTitleParts[1];
        }

        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_id]",
          variant.id
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_category]",
          variant.category
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_handle]",
          variant.handle || ""
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_original_price]",
          window.formatMoney(variant.price, window.moneyFormat)
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_title]",
          variantTitle
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_option]",
          variantOption
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_image]",
          variant.image
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_current_value]",
          variant.quantity
        );
        asideProductMiniCard = asideProductMiniCard.replaceAll(
          "[variant_max_available]",
          variant.inventory_quantity
        );
        productMiniCards += asideProductMiniCard;
      });

    if (target && productMiniCards) {
      target.insertAdjacentHTML("beforeend", productMiniCards);

      // Add event listeners to the newly created buttons
      this.attachMiniCardEventListeners(target);
    }
  }

  attachMiniCardEventListeners(container) {
    // Find all newly added mini-cards that don't have event listeners yet
    const newMiniCards = container.querySelectorAll(
      "mm-product-card:not([data-listeners-attached])"
    );

    newMiniCards.forEach((miniCard) => {
      const variantId = miniCard.dataset.mixProduct;
      const category = miniCard.dataset.category;
      const quantity = miniCard.getAttribute('qnt');
      const handle =
        miniCard.dataset.handle || this.getHandleFromVariant(variantId);

      // Get plus and minus buttons
      const plusBtn = miniCard.querySelector(".js-plus");
      const minusBtn = miniCard.querySelector(".js-minus");
      const removeBtn = miniCard.querySelector(".js-remove");

      if (plusBtn) {
        plusBtn.addEventListener("click", () => {
          this.updateMiniCardQuantity(variantId, category, handle, 1);
        });
      }

      if (minusBtn) {
        minusBtn.addEventListener("click", () => {
          this.updateMiniCardQuantity(variantId, category, handle, -1);
        });
      }

      if (removeBtn) {
        removeBtn.addEventListener("click", () => {
          this.updateMiniCardQuantity(variantId, category, handle, -quantity);
        });
      }

      // Mark as having listeners attached
      miniCard.setAttribute("data-listeners-attached", "true");
    });
  }

  updateMiniCardQuantity(variantId, category, handle, quantityChange) {
    if (!window.mixMatchStore) return;

    const state = window.mixMatchStore.getState();
    const variantKey = `${category}_${variantId}`;
    const variant = state.variants[variantKey];

    if (!variant) {
      console.warn("Variant not found in state:", variantKey);
      return;
    }

    // Don't allow going below 0
    if (variant.quantity + quantityChange < 0) {
      return;
    }

    window.mixMatchStore.updateVariantQuantity({
      id: variantId,
      category: category,
      price: variant.price,
      title: variant.title,
      image: variant.image,
      quantity: quantityChange,
      type: variant.type || "product",
      handle: handle,
    });
  }

  getHandleFromVariant(variantId) {
    // Try to find the handle from existing product cards
    const productCards = document.querySelectorAll(
      'mm-product-card[data-mix-product="' + variantId + '"]'
    );
    for (const card of productCards) {
      if (card.dataset.handle) {
        return card.dataset.handle;
      }
    }

    // Fallback: try to find in state
    if (window.mixMatchStore) {
      const state = window.mixMatchStore.getState();
      for (const [key, variant] of Object.entries(state.variants)) {
        if (key.includes(`_${variantId}`) && variant.handle) {
          return variant.handle;
        }
      }
    }

    return "";
  }

  getVariantDisplayTitle(variant) {
    // Try to get variant options from the product cards
    const productCards = document.querySelectorAll(
      `mm-product-card[data-handle="${variant.handle}"]`
    );

    for (const card of productCards) {
      try {
        const availableVariants = JSON.parse(card.dataset.variants || "[]");
        const variantData = availableVariants.find((v) => v.id == variant.id);

        if (
          variantData &&
          variantData.options &&
          variantData.options.length
        ) {
          // Show product title + variant options (e.g., "Product Name - Size M, Color Red")
          const options = variantData.options
            .filter((option) => option !== "Default Title")
            .join(", ");
          return options ? `${variant.title} - ${options}` : variant.title;
        }
      } catch (e) {
        console.warn("Error parsing variant data:", e);
      }
    }

    // Fallback to just the variant title
    return variant.title;
  }

  getGiftGoalQuantity(state, giftHandle) {
    return state.gifts[giftHandle]?.goal_quantity;
  }

  updateGiftUI(state) {
    const totalQuantity = state.totalQuantity;
    const giftBlocks = this.querySelectorAll(".js-gift-block");

    if (giftBlocks.length == 0) {
      console.warn("No gift blocks found");
      return;
    }

    giftBlocks.forEach((el) => {
      const giftHandle = el.getAttribute("data-gift-handle");
      const goalQuantity = this.getGiftGoalQuantity(state, giftHandle);

      if (isNaN(goalQuantity)) {
        console.warn("Goal quantity is not a number");
        return;
      }

      if (totalQuantity >= goalQuantity) {
        this.querySelectorAll(".js-gift-empty:not(.jc-hidden)").forEach(
          (el) => {
            el.classList.add("jc-hidden");
          }
        );
        this.querySelectorAll(".js-gift-full.jc-hidden").forEach((el) => {
          el.classList.remove("jc-hidden");
        });
      } else {
        this.querySelectorAll(".js-gift-empty.jc-hidden").forEach((el) => {
          el.classList.remove("jc-hidden");
        });
        this.querySelectorAll(".js-gift-full:not(.jc-hidden)").forEach((el) => {
          el.classList.add("jc-hidden");
        });
      }
    });
  }

  updateGiftCheckedState(value, giftHandle) {
    window.mixMatchStore.updateGiftCheckedState({
      gift_qnt: value,
      gift_handle: giftHandle,
    });
  }

  updateTotalQuantity(state) {
    if (this.totalQuantity.length > 0) {
      this.totalQuantity.forEach((el) => {
        el.textContent = state.totalQuantity;
        el.setAttribute("data-total-quantity", state.totalQuantity);
      });
    }
  }

  updateTotalPrices(state) {
    // Met à jour tous les éléments .js-total-price
    document.querySelectorAll('.js-total-price').forEach(el => {
      el.textContent = window.formatMoney(
        state.totalPrice || 0,
        window.moneyFormat
      );
    });
  
    // Met à jour TOUS les éléments du prix barré
    document.querySelectorAll('.js-total-compare-price, .js-total-price-without-discount').forEach(el => {
      if (
        state.totalPriceWithoutDiscount === 0 ||
        state.totalPriceWithoutDiscount === state.totalPrice
      ) {
        el.textContent = '';
      } else {
        el.textContent = window.formatMoney(
          state.totalPriceWithoutDiscount,
          window.moneyFormat
        );
      }
    });
  }




  updateButtonsState(state) {
    if (state.minimumQuantity <= 0) {
      state.minimumQuantity = 1
    }

    if (this.mainAddBtns.length > 0) {
      if (state.totalQuantity < state.minimumQuantity) {
        this.mainAddBtns.forEach((el) => {
          el.classList.add("disabled");
        });
      } else {
        this.mainAddBtns.forEach((el) => {
          el.classList.remove("disabled");
        });
      }
    }
  }

  updateMessages(state) {
    const currentDiscount = state.currentDiscount;
    const currentQuantity = state.totalQuantity;

    if (isNaN(currentDiscount) || isNaN(currentQuantity)) return;

    const { discountTiers } = state;

    const higherDiscountIndex = discountTiers.findIndex(
      (item) => item.discount > currentDiscount
    );
    const exactDiscountIndex = discountTiers.findIndex(
      (item) => item.discount === currentDiscount
    );

    const tierIndex =
      higherDiscountIndex !== -1 ? higherDiscountIndex : exactDiscountIndex;
    if (tierIndex === -1) return;

    const nextTier = discountTiers[tierIndex];
    const requiredQuantity = Math.max(0, nextTier.quantity);
    const remainingQuantity = Math.max(0, requiredQuantity - currentQuantity);
    const futureDiscount = nextTier.discount;

    const totalQuantity = state.totalQuantity;
    const firstGiftBlock = this.querySelector(".js-gift-block");
    const firstGiftHandle =
      firstGiftBlock?.getAttribute("data-gift-handle") || "";
    const goalQuantity = this.getGiftGoalQuantity(state, firstGiftHandle);
    const giftGoalDifference = Math.max(0, goalQuantity - totalQuantity);

    this.querySelectorAll(".js-messages-wrap").forEach((wrapper) => {
      // Replace placeholders with spans (only once)
      wrapper.innerHTML = wrapper.innerHTML
        .replaceAll("[discount]", '<span class="js-closest-discount"></span>')
        .replaceAll("[number]", '<span class="js-closest-product"></span>')
        .replaceAll("[gift_number]", '<span class="js-gift-number"></span>')
        .replaceAll("<em>produits</em>", "produit<span class='js-single-product'>s</span>");

      if (!wrapper.classList.contains('js-progress-text')) {
        wrapper.querySelectorAll(".js-closest-product").forEach((el) => {
          el.textContent = remainingQuantity == 1 ? requiredQuantity : remainingQuantity;
        });
      }
      else {
        wrapper.querySelectorAll(".js-closest-product").forEach((el) => {
          el.textContent = remainingQuantity;
        });
      }

      // Update numbers
      
      wrapper.querySelectorAll(".js-closest-discount").forEach((el) => {
        el.textContent = futureDiscount;
      });
      wrapper.querySelectorAll(".js-gift-number").forEach((el) => {
        el.textContent = giftGoalDifference;
      });
      wrapper.querySelectorAll(".js-single-product").forEach((el) => {
        remainingQuantity == 1 ? el.classList.add('jc-hidden') : el.classList.remove('jc-hidden')
      });

      // Update text
      const singleText = wrapper.querySelector(".js-single-product-text");
      const multipleText = wrapper.querySelector(".js-multiple-product-text");
      const singleAddAndGetText = wrapper.querySelector(
        ".js-single-add-and-get"
      );
      const multipleAddAndGetText = wrapper.querySelector(
        ".js-multiple-add-and-get"
      );
      const reachedText = wrapper.querySelector(".js-riched-max");
      const messagesWrap =
        this.querySelector(".js-messages-wrap")?.parentElement;
      const wrapParent = wrapper.closest(
        ".js-messages-wrap:not(.js-progress-text)"
      )?.parentElement;
      const progressText = wrapper.querySelector(".js-discount-text");

      const isSingle = remainingQuantity === 1;
      const isNone = remainingQuantity === 0;
      const closeToGift = giftGoalDifference < 4;

      if (
        wrapper.classList.contains("js-gift-text") &&
        !closeToGift &&
        multipleAddAndGetText
      ) {
        // Show gift-related text if is not close to gift goal
        multipleAddAndGetText?.classList.remove("jc-hidden");
      } else if (
        wrapper.classList.contains("js-progress-text") &&
        closeToGift &&
        giftGoalDifference > 0 &&
        multipleAddAndGetText &&
        singleAddAndGetText
      ) {
        const isGiftSingle = giftGoalDifference === 1;

        // Toggle gift-related text
        singleAddAndGetText?.classList.toggle("jc-hidden", !isGiftSingle);
        multipleAddAndGetText?.classList.toggle("jc-hidden", isGiftSingle);

        // Hide regular discount texts in gift context
        singleText?.classList.add("jc-hidden");
        multipleText?.classList.add("jc-hidden");
      } else {
        // Regular logic for discount thresholds
        singleText?.classList.toggle("jc-hidden", !isSingle);
        multipleText?.classList.toggle("jc-hidden", isSingle);

        // Hide gift-related text in regular context
        singleAddAndGetText?.classList.add("jc-hidden");
        multipleAddAndGetText?.classList.add("jc-hidden");
      }

      // Handle progress text visibility - only if this wrapper is a progress text
      if (wrapper.classList.contains('js-progress-text')) {
        const startText = wrapper.querySelector('.js-start-text');
        const discountText = wrapper.querySelector('.js-discount-text');
        const isEmpty = state.totalQuantity <= 0;
        
        startText?.classList.toggle('jc-hidden', !isEmpty);
        discountText?.classList.toggle('jc-hidden', isEmpty);
      }

      // Handle 'reached' state
      if (isNone) {
        wrapper.querySelectorAll(
          ".js-single-product-text, .js-multiple-product-text, .js-progress-wrapper:has(.js-discount-text)"
        ).forEach((el) => el.classList.add("jc-hidden"));

        reachedText?.classList.remove("jc-hidden");
        wrapParent?.classList.add("jc-hidden");
      } else {
        messagesWrap?.classList.remove("jc-hidden");
        reachedText?.classList.add("jc-hidden");
        wrapParent?.classList.remove("jc-hidden");
        progressText?.parentNode.classList.remove("jc-hidden");
      }
    });
  }

  updateProgressBar(state) {
    // update progress bar
    if (this.progress) {
      this.progress.forEach((el) => {
        const progressItems = el.querySelectorAll(".js-bundle-progress-item");
        const giftProgressItems = el.querySelectorAll(
          ".js-bundle-gift-progress-item"
        );
        const freeShippingProgressItem = el.querySelector(".js-free-shipping");
        const currentDiscount = state.currentDiscount;
        const currentQuantity = state.totalQuantity;

        if (isNaN(currentDiscount) || isNaN(currentQuantity)) {
          return;
        }

        const higherDiscountIndex = state.discountTiers.findIndex(
          (item) => item.discount > currentDiscount
        );
        const exactDiscountIndex = state.discountTiers.findIndex(
          (item) => item.discount == currentDiscount
        );
        const tierIndex =
          higherDiscountIndex === -1 ? exactDiscountIndex : higherDiscountIndex;
        if (tierIndex === -1) return;

        const nextTier = state.discountTiers[tierIndex];
        const requiredQuantity = Math.max(0, nextTier.quantity);
        const remainingQuantity = Math.max(
          0,
          requiredQuantity - currentQuantity
        );
        const futureDiscount = nextTier.discount;

        progressItems.forEach((item, index) => {
          const itemDiscount = parseFloat(item.dataset.discount);
          const itemFutureDiscount = parseFloat(item.dataset.futureDiscount);

          if (isNaN(itemDiscount) || isNaN(itemFutureDiscount)) {
            return;
          }

          let progressPercentage = 0;
          let discountProgress;

          if (currentDiscount >= itemDiscount) {
            discountProgress = "100%";
          } else if (futureDiscount === itemDiscount) {
            // For the next tier, calculate progress based on current tier's requirements
            const currentTierIndex = state.discountTiers.findIndex(
              (tier) => tier.discount === itemDiscount
            );
            
            if (currentTierIndex > 0) {
              const currentTierRequired = state.discountTiers[currentTierIndex].quantity;
              const previousTierRequired = state.discountTiers[currentTierIndex - 1].quantity;
              const tierDifference = currentTierRequired - previousTierRequired;
              
              // Calculate progress within this tier
              const progressInTier = Math.max(0, currentQuantity - previousTierRequired);
              progressPercentage = Math.min(100, (progressInTier / tierDifference) * 100);
            } else {
              // For the first tier, calculate based on total required
              progressPercentage = Math.min(100, (currentQuantity / requiredQuantity) * 100);
            }
            
            discountProgress = `${progressPercentage}%`;
          } else {
            discountProgress = "0%";
          }

          item.style.setProperty("--discountProgress", discountProgress);
          item.dataset.progress = discountProgress;
        });

        const totalQuantity = state.totalQuantity;

        if (giftProgressItems.length > 0) {
          giftProgressItems.forEach((item) => {
            const giftHandle = item.getAttribute("data-gift-handle");
            const goalQuantity = this.getGiftGoalQuantity(state, giftHandle);
            if (isNaN(goalQuantity)) {
              console.warn("Goal quantity is not a number");
              return;
            }
            const qntDiff = totalQuantity - goalQuantity;
            let giftProgress = "0%";

            if (qntDiff >= 0) {
              giftProgress = "100%";
            } else if (qntDiff > -4) {
              // Close to unlocking: 1 away = 75%, 2 away = 50%, 3 away = 25%
              giftProgress = `${(4 + qntDiff) * 25}%`;
            }

            item.style.setProperty("--discountProgress", giftProgress);
            item.dataset.progress = giftProgress;
          });
        } else {
          console.warn("Gift progress bar element not found");
        }

        if (freeShippingProgressItem) {
          const cartTotal = parseFloat(
            freeShippingProgressItem.dataset.cartTotal
          );

          const freeShippingThreshold = parseFloat(
            freeShippingProgressItem.dataset.freeShippingThreshold
          );
          if (isNaN(freeShippingThreshold) || isNaN(cartTotal)) {
            return;
          }

          const hitTresholdForFreeShipping =
            state.totalPrice + cartTotal >= freeShippingThreshold;

          freeShippingProgressItem.classList.toggle(
            "active",
            hitTresholdForFreeShipping
          );
        }
      });
    } else {
      console.warn("Progress bar element not found");
    }
  }

  static async removeBundle(key) {
    let updates = {};
    updates[key] = 0;

    const response = await fetch(
      window.Shopify.routes.root + "cart/update.js",
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: updates,
        }),
      }
    );

    if (!response.ok) {
      console.error("Error removing bundle from cart");
    }
  }

  async addToCart() {
    // Find the appropriate add to cart button (regular or drawer)
    const regularAddBtn = this.querySelector(".js-add-mix-and-match");
    const drawerAddBtn = this.querySelector(".js-drawer-add-to-cart");
    const activeAddBtn = drawerAddBtn && this.bundleDrawer && this.bundleDrawer.classList.contains('jc-visible')
      ? drawerAddBtn
      : regularAddBtn;

    if (!activeAddBtn) {
      console.error('No add to cart button found');
      return;
    }

    activeAddBtn.ariaBusy = true;
    const state = window.mixMatchStore.getState();
    const bundleKeyToRemove = activeAddBtn?.getAttribute("data-mm-add-to-cart") || "";
    const bundleId = JSON.parse(window.sessionStorage.getItem("mixBundleId") || '""');
    const currentDate = Date.now();
    const identifier = `${bundleId}_${currentDate}`;

    const addToCartFormData = { items: [], sections: ['mm-cart'] };

    if (bundleKeyToRemove != "" && bundleKeyToRemove != null) {
      await MMCartSummary.removeBundle(bundleKeyToRemove);
      activeAddBtn.setAttribute("data-mm-add-to-cart", "");
    }

    const bundleLineItemProperties = [];
    // Preparing line Item Properties
    Object.values(state.variants)
      .filter((variant) => variant.quantity !== 0)
      .forEach((variant) => {
        bundleLineItemProperties.push({
          variantId: variant.id,
          quantity: variant.quantity,
          category: variant.category || "",
          type: variant.type,
          properties: {
            _handle: variant.handle,
          },
        });
      });

    // Add eligible gifts
    Object.values(state.gifts)
      .filter(
        (gift) =>
          state.totalQuantity >= gift.goal_quantity && gift.gift_quantity > 0
      )
      .forEach((gift) => {
        bundleLineItemProperties.push({
          variantId: gift.id,
          quantity: gift.gift_quantity,
          category: "",
          type: gift.type,
          properties: {
            _handle: gift.handle,
          },
        });
      });

    let productList = bundleLineItemProperties
      .map((product) => `${product.properties._handle}:${product.variantId}`)
      .join(",");

    // Create addons state string
    const allGiftHandles = Object.keys(state.gifts);
    const addonsList = bundleLineItemProperties.filter(
      (product) => product.type === "addOns"
    );
    const cartGiftMap = {};
    addonsList.forEach((product) => {
      const handle = product.properties._handle;
      const quantity = product.quantity || 0;
      cartGiftMap[handle] = quantity;
    });

    const addonsListString = allGiftHandles
      .map((handle) => `${handle}:${cartGiftMap[handle] ?? 0}`)
      .join(",");

    function getCurrentVariantIdBundle() {
      const mixDiscount = () =>
        JSON.parse(window.sessionStorage.getItem("mixDiscount"));
      const mixDiscountObject = mixDiscount();
      const indexCurrentDiscount = mixDiscountObject.findLastIndex(
        (discount) =>
          state.totalQuantity >= discount[Object.keys(discount)].quantity
      );
      if (indexCurrentDiscount === -1) {
        throw Error("Matching option not found");
      }

      return Object.keys(mixDiscountObject[indexCurrentDiscount])[0];
    }

    const currentBundleId = getCurrentVariantIdBundle().split("/").pop();

    // Preparing Add to Cart Data
    addToCartFormData.items.push({
      id: currentBundleId,
      quantity: 1,
      properties: {
        "__foxsell:dynamic_add_on_bundle_id": identifier,
        "__foxsell:dynamic_add_on_bundle_items": JSON.stringify(
          bundleLineItemProperties
        ),
        _bundle_products: `${productList}`,
        _bundle_addons: `${addonsListString}`,
        _bundle: true,
        _bundle_price: state.totalPriceWithoutDiscount,
        _bundle_discount: state.currentDiscount,
        _bundle_qnt: state.totalQuantity,
        _bundle_id: identifier,
      }
    });

    // regular shopify add to cart
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(addToCartFormData),
      });

      // Splide init
      function mmInitSplide() {
        const id = '.splide-mm-cart';
        const splideElem = document.querySelector(id);
        const idrea = '.splide-cta-testimonials';
        const splideElemrea = document.querySelector(idrea);

        if (splideElem) {
          new Splide(splideElem, {
            type: 'splide',
            arrows: true,
            pagination: false,
            rewind: false,
            gap: 20,
            perPage: 2,
            direction: 'ttb',
            height: '100%',
            destroy: true,
            breakpoints: {
              701: {
                destroy: false,
                rewind: true,
                direction: 'ltr',
                perPage: 1,
                padding: {
                  left: '30px',
                  right: '60px',
                },
              },
            },
          }).mount();
        }

        if (splideElemrea) {
          new Splide(splideElemrea, {
            type: 'loop',
            arrows: true,
            pagination: false,
            gap: 0,
            perPage: 1,
            autoplay: true,
            interval: 3000,
          }).mount();
        }
      }

      function mmOpenCart() {
        mmGetCart();
        document.querySelector('.mm-drawer-cart-filter').classList.add('mm-dc-is-open');
        if (document.querySelector('.mobileMenu').classList.contains('mm-menu-open')) {
          document.querySelector('.mobileMenu').classList.remove('mm-menu-open');
        }
        if (document.querySelector('.sidebarBgg').classList.contains('mm-menu-open')) {
          document.querySelector('.sidebarBgg').classList.remove('mm-menu-open');
        }
        if (document.querySelector('.sidebarMenu').classList.contains('mm-menu-open')) {
          document.querySelector('.sidebarMenu').classList.remove('mm-menu-open');
        }
        if (document.getElementById('mmSearchMobileWrapper').classList.contains('mm-active')) {
          document.getElementById('mmSearchMobileWrapper').classList.remove('mm-active');
        }
        document.querySelector('html').classList.add('stop-scrolling');
      }

      function mmCartSectionElement() {
        return document.getElementById('shopify-section-mm-cart');
      }

      function refreshCartFromData(data) {
        const newCartEl = document.createElement('DIV');
        newCartEl.innerHTML = data.sections["mm-cart"];
        const innerCart = mmCartSectionElement().querySelector(".mm-drawer-cart-filter");
        innerCart.innerHTML = newCartEl.querySelector(".mm-drawer-cart-filter").innerHTML;
        mmInitSplide();
        mmOpenCart();
      }

      function mmAddOrRemoveFreeProducts(data) {
        if (data.sections['mm-cart']) {
            const el = mmCartHTMLFromData(data);
            const freeProductEls = el.querySelector('[data-free-products]');
            
            if (!freeProductEls) return data;
            
            // Group products by index (1 or 2)
            const productsByIndex = Array.from(freeProductEls.children).reduce((groups, el) => {
                const index = el.dataset.index;
                if (!groups[index]) groups[index] = [];
                groups[index].push({
                    id: el.dataset.variantId,
                    available: el.dataset.available === 'true',
                    isFree: el.dataset.freeProduct === 'true'
                });
                return groups;
            }, {});
    
            // Find first available product for each index
            for (const index of ['1', '2']) {
                if (productsByIndex[index]) {
                    const availableProduct = productsByIndex[index].find(p => p.available);
                    if (availableProduct && !localStorage.getItem(`free_product_choice_${index}`)) {
                        localStorage.setItem(`free_product_choice_${index}`, availableProduct.id);
                    }
                }
            }
    
            let freeProductCounts = 0;
            const productsParams = Array.from(freeProductEls.children).reduce((param, freeProductEl) => {
                const productId = freeProductEl.dataset.variantId;
                const isFree = freeProductEl.dataset.freeProduct === 'true';
                const isAvailable = freeProductEl.dataset.available === 'true';
                const itemEl = mmCartItem(productId);
                const freeProductName = 'free_product_cancelled_' + productId;
    
                if (!isAvailable) return param;
    
                // Rest of your existing logic
                if (freeProductEl.dataset.freeProductAmount * 100 > el.querySelector('.mm-drawer-cart').dataset.cartTotal) {
                    localStorage.setItem(freeProductName, 'false');
                }
    
                if (isFree) freeProductCounts += 1;
    
                if (itemEl && isFree) {
                    return param;
                } else if (itemEl && !isFree) {
                    param[productId] = 0;
                } else if (!itemEl && isFree) {
                    if (!localStorage.getItem(freeProductName)) {
                        localStorage.setItem(freeProductName, 'false');
                    }
                    
                    const isSelected = localStorage.getItem(freeProductName) === 'false' && 
                        (localStorage.getItem('free_product_choice_1') === productId || 
                         localStorage.getItem('free_product_choice_2') === productId);
                    
                    if (isSelected) {
                        param[productId] = 1;
                    }
                }
                return param;
            }, {});
    
            if (Object.keys(productsParams).length > 0) {
                mmAPIRequest('POST', 'cart/update.js', { 
                    updates: productsParams, 
                    sections: 'mm-cart' 
                }).then(refreshCartFromData);
            }
        }
        return data;
      }

      const json = await response.json();

      if (response.ok) {
        window.mixMatchStore.clearState();
        window.mixMatchStore.updateTotals();
        window.mixMatchStore.persist();
        window.mixMatchStore.notify("UPDATE_VARIANT_QUANTITY");
        window.mixMatchStore.notify("GIFT_CHECKED_STATE_UPDATED");
        activeAddBtn.ariaBusy = false;

        mmAddOrRemoveFreeProducts(json);
        refreshCartFromData(json);

        // Close drawer if add to cart was triggered from drawer
        if (drawerAddBtn && this.bundleDrawer && this.bundleDrawer.classList.contains('jc-visible')) {
          this.closeDrawer();
        }
      } else {
        throw new Error(`${json?.message}: ${json?.description}`);
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      activeAddBtn.ariaBusy = false;
      throw error;
    }
  }

  onStateChange({ action, previousState, state }) {
    this.addProductMiniCards(state);
    this.updateGiftUI(state);
    this.updateTotalQuantity(state);
    this.updateTotalPrices(state);
    this.updateButtonsState(state);
    this.updateProgressBar(state);
    this.updateMessages(state);
    this.updateBottomCards();

    // Update drawer if it's open
    if (this.bundleDrawer && this.bundleDrawer.classList.contains('jc-visible')) {
      this.populateDrawerProducts();
    }
  }

  // Throttle function to limit the rate at which a function can fire
  throttle(fn, wait) {
    let lastTime = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastTime >= wait) {
        lastTime = now;
        fn.apply(this, args);
      }
    };
  }

  // Remove listeners if element is removed
  disconnectedCallback() { }
}

class NavBlock extends HTMLElement {
  constructor() {
    super();
    this.throttledScrollSpy = this.throttle(this.trackPositionAndStatus.bind(this), 100);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick.bind(this));

    // Update tracking
    this.trackPositionAndStatus();
    document.addEventListener("scroll", () => {
      this.trackPositionAndStatus();
    });
  }

  handleClick() {
    // Update active state
    document
      .querySelectorAll("mm-nav-block")
      .forEach((el) => el.classList.remove("active"));
    this.classList.add("active");

    // Get target category
    const targetCategoryTitle = this.getAttribute("data-scroll");
    const slug = this.slugify(targetCategoryTitle);
    const targetElement = document.querySelector(`#category-${slug}`);
    const navElement = this.closest("#topNavigation");

    if (!targetElement || !navElement) return;

    const navigationHeight = navElement.offsetHeight;

    // Open the accordion first
    if (window.mixMatchAccordion) {
      window.mixMatchAccordion.openAccordion(slug);
    }

    const scrollFromTop = targetElement.getBoundingClientRect().top + window.scrollY - navigationHeight;

    // Scroll with offset
    window.scrollTo({
      top: scrollFromTop - 20,
      behavior: "smooth",
    });
  }

  trackPositionAndStatus() {
    const navBlocks = document.querySelectorAll("mm-nav-block");
    const sections = Array.from(document.querySelectorAll("[data-nav-title]"));
    const topNav = document.getElementById("topNavigation");

    if (topNav === null) return;

    const visible = this.getVisibleSection(sections);
    if (!visible) return;

    navBlocks.forEach((block) => {
      if (block.getAttribute("data-scroll") === visible) {
        block.classList.add("active");
      } else {
        block.classList.remove("active");
      }
    });
  }

  getVisibleSection(sections) {
    const scrollY = window.scrollY;
    const viewportTrackPoint = scrollY + window.innerHeight * 0.6;
  
    for (const section of sections) {
      if (section.offsetHeight < 10) continue;
  
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top + scrollY;
      const sectionBottom = sectionTop + section.offsetHeight;
  
      if (sectionTop <= viewportTrackPoint && sectionBottom >= viewportTrackPoint) {
        return section.getAttribute("data-nav-title");
      }
    }

    return null;
  }

  slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  }

  throttle(fn, wait) {
    let lastTime = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastTime >= wait) {
        lastTime = now;
        fn.apply(this, args);
      }
    };
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.throttledScrollSpy);
  }
}

// Initialize everything
document.addEventListener("DOMContentLoaded", () => {
  // Create store instance
  if (!document.querySelector("mm-product-card")) {
    return;
  }
  window.mixMatchStore = new MixAndMatchStore();

  // Create accordion manager instance
  window.mixMatchAccordion = new MMAccordionManager();

  // Register custom elements
  customElements.define("mm-product-card", MMProductCard);
  customElements.define("mm-cart-summary", MMCartSummary);
  customElements.define("mm-nav-block", NavBlock);

  document.querySelectorAll("mm-product-card").forEach((card) => {
    card.addEventListener("mm-add-to-cart", (event) => {
      window.mixMatchStore.addToCart(event.detail);
    });
  });
});

document.addEventListener('DOMContentLoaded', function () {
  const navigation = document.getElementById('topNavigation');
  const id = `.splide-${navigation.dataset.sectionId}`;
  const splideElem = document.querySelector(id);
  
  if (splideElem) {
    new Splide(splideElem, {
      type: 'slide',
      arrows: false,
      pagination: false,
      gap: 12,
      perPage: 'auto',
      perMove: 1,
      focus: 'start',
      trimSpace: true,
      breakpoints: {
        768: {
          arrows: false,
          gap: 8
        }
      }
    }).mount();
  }
});